import { NO_QA_MESSAGE, STATUS_TYPES } from 'helpers/sf_constants'
import { getPrimaryButtonClass, getPrimaryTextClass, getQALabel } from 'helpers/sf_utils'
import useClearableTimeout from 'hooks/useClearableTimeout'
import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Col, Input, UncontrolledTooltip } from 'reactstrap'
import { getAnswerFromQuery } from 'store/call-summary-qa/actions'
const ANSWER_SPLIT_REGEX = /\n-|\n -|\n/
const BULLET_POINT_SYMBOL = "\u2022";
const EMPTY_ENTRY_REGEX = '^(-|\s)*$'
const QASection = ({ call_summary_qa, lead_id, setOverallConversation, status, call_summary }) => {

    const { answer_to_query, answer_to_query_loading, error, summary_error } = useSelector((state) => ({
        answer_to_query: state.AnswerFromQuery.answer_to_query,
        answer_to_query_loading: state.AnswerFromQuery.answer_to_query_loading,
        error: state.AnswerFromQuery.error,
        summary_error: state.CallSummary.error,
    }))
    const [userQuery, setUserQuery] = useState(null)
    const [conversation, setConversation] = useState([])
    const [showError, setShowError] = useState()
    const dispatch = useDispatch()
    const [customTimeoutSet] = useClearableTimeout();
    const qaContainerRef = useRef(null)
    const isConversationPage = (window.location.href.includes("/conversations"))
    const isCallSummaryPending = status === STATUS_TYPES.IN_PROGRESS || !call_summary || !status
    const sendUserQueryToAI = () => {
        if (userQuery) {
            dispatch(getAnswerFromQuery({ "lead_id": lead_id, "prompt": userQuery }))
            setConversation(prevConversation => [...prevConversation, { question: userQuery, answer: null }])
            setUserQuery(null)
        }
    }
    useEffect(() => {
        setConversation(call_summary_qa || [])
    }, [call_summary_qa])

    const handleFlashMessage = () => {
        customTimeoutSet(() => {
            setShowError(false)
        }, 3000);
    };

    useEffect(() => {
        if (error) {
            setShowError(true)
            handleFlashMessage()
        }
    }, [error])

    useEffect(() => {
        setShowError(false)
    }, [summary_error])

    useEffect(() => {
        const updatedConversation = [...conversation];
        const lastIndex = updatedConversation.length - 1;
        if (updatedConversation[lastIndex]?.answer === null && answer_to_query?.answer) {
            const answerChunks = answer_to_query.answer.split(' ');
            let displayedText = '';
            let currentIndex = 0;
            const typingInterval = setInterval(() => {
                if (currentIndex < answerChunks.length) {
                    displayedText += (currentIndex === 0 ? '' : ' ') + answerChunks[currentIndex];
                    setConversation(prevConversation => {
                        const updatedConversation = [...prevConversation];
                        updatedConversation[lastIndex] = {
                            ...updatedConversation[lastIndex],
                            answer: displayedText
                        };
                        return updatedConversation;
                    });
                    currentIndex++;
                } else {
                    clearInterval(typingInterval);
                }
            }, 10);
        }
    }, [answer_to_query]);

    useEffect(() => {
        if (qaContainerRef.current) {
            qaContainerRef.current.scrollTop = qaContainerRef.current.scrollHeight
        }
    }, [conversation])

    useEffect(() => {
        if (isConversationPage) {
            setOverallConversation(conversation)
        }
    }, [conversation])

    return (
        <React.Fragment>
            <div id="qa-section">
                <div className="d-flex align-items-center font-weight-semibold mt-4 mb-2">
                    <div className="d-flex">
                        <i className={`bx bxs-comment-detail ${getPrimaryTextClass()} font-size-26 me-2 bx-sm`} />
                        <h1 className="text-black text-start fs-16 fw-bold">Q&A</h1>
                        <p className={`${getPrimaryTextClass()} font-size-12 ms-2 fw-600`}>{getQALabel()}</p>
                        <i id='qa-tooltip' className='ms-1 bx bx-info-circle text-black bx-xs'></i>
                    </div>
                    <UncontrolledTooltip placement="top" target='qa-tooltip'>
                        Ask questions about the call
                    </UncontrolledTooltip>
                </div>
                <div>
                    <div className='mb-4' style={{ maxHeight: '400px', overflowY: 'auto', minHeight: '140px' }} ref={qaContainerRef}>
                        {conversation.length > 0 ?
                            conversation.sort((a, b) => {
                                return new Date(a.updated_at) - new Date(b.updated_at);
                            }).map(({ question, answer }, index) => {
                                const splitAnswers = answer?.split(ANSWER_SPLIT_REGEX)
                                // filter out answers which are blank
                                const splitAnswersOut = splitAnswers?.filter(answer => !answer?.match(EMPTY_ENTRY_REGEX))
                                return (
                                    <React.Fragment key={index}>
                                        <div className='custom-border-for-qa-question fw-600 me-2 mt-3'>
                                            {question}
                                        </div>
                                        {answer && (
                                            <div className='custom-border-for-qa-answers me-2 mt-3'>
                                                {splitAnswersOut.length >= 2 ? (
                                                    splitAnswersOut.map((splitAnswer, splitIndex) => (
                                                        <ul style={{margin: '0px'}} key={splitIndex} className='ps-4 mb-1'>
                                                            <li key={splitIndex} className="">
                                                                {splitAnswer}
                                                            </li>
                                                        </ul>
                                                    ))
                                                ) : (
                                                    <div className='ps-3'>{answer.replace("-", "")}</div>
                                                )}
                                            </div>
                                        )}
                                    </React.Fragment>
                                );
                            })
                            : <div className='ms-1' >{NO_QA_MESSAGE}</div>
                        }
                        {answer_to_query_loading ? (
                            <div className={`me-2 mt-3 ms-1 custom-border-for-qa-answers ps-2 ${answer_to_query_loading ? 'conversation-answer-loading' : ''}`}>
                                {'Your answer is on its way'}
                            </div>
                        ) : null}
                        {
                            showError && <div className='me-2 mt-3 text-danger ms-1'>Something went wrong.</div>
                        }
                    </div>
                    <Col md={8}>
                        <div className="d-flex justify-content-end mb-1">
                            <Input
                                type="text"
                                className="me-2"
                                disabled={isCallSummaryPending}
                                placeholder="Ask anything about the call..."
                                value={userQuery || ""}
                                onChange={(event) => setUserQuery(event.target.value)}
                                onKeyPress={(event) => {
                                    if (event.key === 'Enter' && !(isCallSummaryPending)) {
                                        sendUserQueryToAI();
                                    }
                                }}
                            />
                            <button className={`btn btn-sm ${getPrimaryButtonClass()}`} id="conversations-ask-button" disabled={answer_to_query_loading || !userQuery || isCallSummaryPending} style={!answer_to_query_loading ? { width: '70px' } : {}} onClick={sendUserQueryToAI}>{answer_to_query_loading ? "Loading..." : "Ask"}</button>
                        </div>
                    </Col>
                </div>
            </div>

        </React.Fragment>
    )
}

export default QASection
